import { Dispatch, SetStateAction } from 'react';
import { api, getToken } from '../DefaultService';
import logger from '../../utils/logger';
import { IPaginationParams } from '../types';
import { IEventPauseUpdateParams } from './types';

export interface WebSocketStreamProps {
  endpoint: string;
  setState: Dispatch<SetStateAction<any[] | any>>;
  pagination?: IPaginationParams;
  onResponse?: () => void;
  setIsLoading?: Dispatch<SetStateAction<boolean>>;
  eventPauseUpdateParams?: IEventPauseUpdateParams;
  isAllByLicense?: boolean;
  filter?: object;
  isBytes?: boolean;
  onSocketError?: (message: string) => void;
}

export const EVENT_ENDPOINT = 'v1/events';

export const getStreamByEndpointAsync = async (
  endpoint: string,
  setState: Dispatch<SetStateAction<any[] | any>>,
  pagination?: IPaginationParams,
  onResponse?: () => void,
  setIsLoading?: Dispatch<SetStateAction<boolean>>,
  eventPauseUpdateParams?: IEventPauseUpdateParams,
  isAllByLicense: boolean = true,
  filter?: object,
  isBytes?: boolean,
  onSocketError?: (message: string) => void,
  additionalParams?: string,
  reconnectAttempt: number = 0,
): Promise<WebSocket | null> => {
  try {
    if (setIsLoading) setIsLoading(true);

    if (!getToken()) {
      return null;
    }
    let socketUrl =
      // @ts-ignore
      `${window._env_.REACT_APP_API_WS_HOST}/${endpoint}${isAllByLicense ? '/getAllByLicense' : ''}/stream?token=Bearer ` +
      window.atob(getToken()!);

    if (additionalParams) socketUrl += `&${additionalParams}`;

    if (pagination) {
      socketUrl += '&pageSize=' + pagination.pageSize;
      socketUrl += '&pageNumber=' + pagination.pageNumber;
      if (pagination.sortBy) socketUrl += '&sortBy=' + pagination.sortBy;
      if (pagination.sortDirection)
        socketUrl += '&sortDirection=' + pagination.sortDirection;
    }

    if (filter) {
      socketUrl +=
        '&' +
        Object.entries(filter)
          .filter(([, value]) => value !== undefined && value !== null)
          .flatMap(([key, value]) =>
            Array.isArray(value)
              ? value.map(
                  (v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`,
                )
              : `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
          )
          .join('&');
    }

    const socket = new WebSocket(socketUrl);
    (socket as any)._isIntentionalClose = false;

    if (isBytes) {
      socket.binaryType = 'arraybuffer';
    }

    const reconnect = () => {
      if ((socket as any)._isIntentionalClose) return;

      const delay = Math.min(1000 * Math.pow(2, reconnectAttempt), 30000);
      logger.debug(`Tentando reconectar em ${delay / 1000}s...`);
      setTimeout(() => {
        getStreamByEndpointAsync(
          endpoint,
          setState,
          pagination,
          onResponse,
          setIsLoading,
          eventPauseUpdateParams,
          isAllByLicense,
          filter,
          isBytes,
          onSocketError,
          additionalParams,
          reconnectAttempt + 1,
        );
      }, delay);
    };

    socket.onclose = () => {
      if (setIsLoading) setIsLoading(false);
      reconnect();
    };

    socket.onerror = (error) => {
      if (setIsLoading) setIsLoading(false);
      reconnect();
    };

    socket.onmessage = (event) => {
      reconnectAttempt = 0;
      const data = isBytes ? event.data : JSON.parse(event.data);

      if (data.httpStatusCode && data.httpStatusCode === 404) {
        if (onSocketError) onSocketError(data.userMessage);
        return;
      }

      if (
        eventPauseUpdateParams &&
        pagination &&
        eventPauseUpdateParams.isPaused
      ) {
        const cachedData = eventPauseUpdateParams.getCacheData(pagination);
        if (cachedData) {
          setState(cachedData);
        } else {
          eventPauseUpdateParams.setCacheData(data, pagination);
          setState(data);
        }
      } else {
        setState(data);
      }

      if (setIsLoading) setIsLoading(false);
      if (onResponse) onResponse();
    };

    return socket;
  } catch (error: any) {
    throw new error();
  }
};

export const getAllStreamCountByLicenseAsync = async (
  endpoint: string,
): Promise<number> => {
  return await api
    .get(`${endpoint}/getAllByLicense/count`)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.message || 'Erro interno do servidor!',
      );
    });
};

export const getAllEventsCountByDeviceIdAsync = async (
  deviceId: number,
): Promise<number> => {
  return await api
    .get(`${EVENT_ENDPOINT}/${deviceId}/count`)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.message || 'Erro interno do servidor!',
      );
    });
};

export const getAllTotalEventAsync = async (
  endpoint: string,
): Promise<number> => {
  return await api
    .get(`${endpoint}`)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.message || 'Erro interno do servidor!',
      );
    });
};
